import React, {useState, useEffect, useRef} from 'react';
import {useParams, useNavigate} from 'react-router-dom';
import PropTypes from 'prop-types';
import unifiedGitDataService from '../services/UnifiedGitDataService';
import indexedDbFileStorage, {GIT_STATE} from '../utils/indexedDbFileStorage';
import commitMessageStorage from '../utils/commitMessageStorage';
import { setGitRef } from '../utils/gitBranchStorage';
import Modal from './Modal';
import Icon from './icons/Icon';
import HexagonalLoader from './HexagonalLoader';
import {getFileIconName} from './icons/fileIcons';
import MonacoSearchPanel from './MonacoSearchPanel';
import './RepositoryTree.css';
import { getAuthorizedFetch } from '../utils/fetchRegistry';

/**
 * Get current tenant ID from localStorage
 * @returns {string|null}
 */
const getTenantId = () => {
  try {
    const stored = localStorage.getItem('mia_selectedTenant');
    if (stored) {
      const tenant = JSON.parse(stored);
      return tenant.tenantId || tenant.id || null;
    }
  } catch (error) {
    console.error('Failed to get tenant ID:', error); /* eslint-disable-line no-console */
  }
  return null;
};


/**
 * RepositoryTree Component - Displays GitLab repository file structure
 * Now responsible for URL synchronization
 */
const RepositoryTree = ({repositoryUrl, onFileSelect, selectedFilePath, serviceName, projectId: miaProjectId, fileErrors, onAskAIToFix, isPullingFromGit, tenantId, branchName}) => {
  const params = useParams();
  const navigate = useNavigate();
  const location = window.location;
  const [tree, setTree] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [expandedFolders, setExpandedFolders] = useState(new Set());
  const [projectId, setProjectId] = useState(null);
  const [ref, setRef] = useState(null); // Branch/tag - will be fetched from project info
  const [refType, setRefType] = useState('branch'); // Track whether it's a branch or tag
  const [projectInfoFetched, setProjectInfoFetched] = useState(false); // Track if we've fetched project info
  const [contextMenu, setContextMenu] = useState(null);
  const [showCreateModal, setShowCreateModal] = useState(false);
  const [createType, setCreateType] = useState('file'); // 'file' or 'folder'
  const [newItemName, setNewItemName] = useState('');
  const [selectedItem, setSelectedItem] = useState(null);
  const [fileStates, setFileStates] = useState({}); // Map of filePath -> gitState
  const [searchQuery, setSearchQuery] = useState('');
  const [activeTab, setActiveTab] = useState('files'); // 'files' or 'search'
  const [showProblemsModal, setShowProblemsModal] = useState(false);
  const [dragOverFolder, setDragOverFolder] = useState(null); // Track folder being dragged over
  const [isDraggingFile, setIsDraggingFile] = useState(false); // Track if dragging files
  const [gitLoading, setGitLoading] = useState(false); // Git operation in progress
  const [gitLoadingMessage, setGitLoadingMessage] = useState(''); // Loading message
  const [gitProgress, setGitProgress] = useState({ current: 0, total: 0 }); // Progress tracking
  const [gitPushFiles, setGitPushFiles] = useState([]); // Files being pushed with their status
  const [gitToolStatus, setGitToolStatus] = useState({ checked: false, toolEnabled: null, authenticated: null, providerName: null }); // Git tool enablement status
  const contextMenuRef = useRef(null);
  const dragCounterRef = useRef(0); // Track drag enter/leave for nested elements
  const lastNavigatedFileRef = useRef(null); // Track last file we navigated to
  const isPullingRef = useRef(false); // Prevent concurrent pulls and pull loops

  /**
   * Check if the git provider tool is enabled on the backend and if the user is authenticated.
   * Returns { toolEnabled: bool, authenticated: bool, providerName: string }
   */
  const checkGitToolStatus = async (repoUrl) => {
    if (!repoUrl) return { toolEnabled: false, authenticated: false, providerName: 'Git' };
    const provider = unifiedGitDataService.detectProvider(repoUrl);
    const providerName = provider === 'github' ? 'GitHub' : provider === 'azuredevops' ? 'Azure DevOps' : 'GitLab';

    try {
      // Check if the tool is enabled on the backend
      let toolEnabled = true; // Optimistic default
      try {
        const { getBffHeaders } = await import('../utils/apiHeaders');
        const { default: config } = await import('../config');
        const bffUrl = config.bffUrl || window.location.origin;
        const resolvedTenantId = tenantId || getTenantId();
        const toolsParams = resolvedTenantId ? `?tenantId=${resolvedTenantId}` : '';
        const toolsResponse = await getAuthorizedFetch()(`${bffUrl}/api/catalog/ai-tools${toolsParams}`, { headers: getBffHeaders() });
        if (toolsResponse.ok) {
          const toolsData = await toolsResponse.json();
          const allTools = toolsData?.data?.items || [];
          // Look for tools belonging to this provider category
          const providerTools = allTools.filter(t => {
            const cat = (t.category || '').toLowerCase();
            const name = (t.name || '').toLowerCase();
            const runtimeName = (t.runtimeName || '').toLowerCase();
            if (provider === 'github') return cat.includes('github') || name.includes('github') || runtimeName.includes('github');
            if (provider === 'azuredevops') return cat.includes('azure') || name.includes('azure') || runtimeName.includes('azure_devops');
            // gitlab (default)
            return cat.includes('gitlab') || name.includes('gitlab') || runtimeName.includes('gitlab');
          });
          // If there are provider tools and ALL of them are disabled → tool is disabled
          if (providerTools.length > 0) {
            toolEnabled = providerTools.some(t => t.enabled !== false);
          }
        }
      } catch (toolsErr) {
        // eslint-disable-next-line no-console
        console.warn('⚠️ Could not check tool status, assuming enabled:', toolsErr.message);
        toolEnabled = true;
      }

      // Check authentication status
      const authStatus = await unifiedGitDataService.checkAuthentication(provider);
      return { toolEnabled, authenticated: authStatus.authenticated, providerName };
    } catch (err) {
      // eslint-disable-next-line no-console
      console.error('❌ Error checking git tool status:', err);
      return { toolEnabled: true, authenticated: false, providerName };
    }
  };

  // Check git tool status whenever the repository URL changes
  useEffect(() => {
    if (!repositoryUrl) return;

    setGitToolStatus({ checked: false, toolEnabled: null, authenticated: null, providerName: null });

    let cancelled = false;
    checkGitToolStatus(repositoryUrl).then(result => {
      if (!cancelled) {
        setGitToolStatus({ checked: true, ...result });
        // eslint-disable-next-line no-console
        console.log(`🔧 Git tool status for ${result.providerName}:`, result);
      }
    });

    return () => { cancelled = true; };
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [repositoryUrl]);

  // Helper function to update URL with file path
  const updateUrlWithFile = (filePath) => {
    if (!tenantId || !miaProjectId || !serviceName || !branchName) {
      // eslint-disable-next-line no-console
      console.warn('⚠️ Cannot update URL - missing params:', {
        tenantId: !!tenantId,
        miaProjectId: !!miaProjectId,
        serviceName: !!serviceName,
        branchName: !!branchName,
        filePath: filePath
      });
      return;
    }
    
    // Mark this file as "we just navigated to it" to prevent URL effect from re-loading it
    lastNavigatedFileRef.current = filePath;
    
    const encodedFilePath = encodeURIComponent(filePath);
    const newPath = `/chat/${tenantId}/${miaProjectId}/${encodeURIComponent(serviceName)}/${encodeURIComponent(branchName)}/code/${encodedFilePath}`;
    // eslint-disable-next-line no-console
    console.log('🔗 Updating URL with file:', filePath, '→', newPath);
    navigate(newPath);
  };

  // Load file from URL ONCE on mount (not on every render)
  useEffect(() => {
    // Extract file path from URL on mount only
    const pathname = location.pathname;
    const match = pathname.match(/\/code\/(.+)$/);
    const urlFilePath = match?.[1] ? decodeURIComponent(match[1]) : null;
    
    // Only load if we have a file path and haven't already loaded it
    if (!urlFilePath || selectedFilePath === urlFilePath) {
      return;
    }
    
    if (miaProjectId && serviceName) {
      // Load the file from URL (this is from browser navigation, not our own navigation)
      // eslint-disable-next-line no-console
      console.log('🔗 Loading file from URL (browser navigation):', urlFilePath);
      
      // Simulate a click on this file
      const loadFileFromUrl = async () => {
        try {
          // Check if file exists in IndexedDB first
          const localFile = await indexedDbFileStorage.getFile(miaProjectId, serviceName, urlFilePath);
          
          if (localFile) {
            const gitState = localFile.gitState || GIT_STATE.MODIFIED;
            onFileSelect({
              path: urlFilePath,
              name: urlFilePath.split('/').pop(),
              content: localFile.content,
              isNew: gitState === GIT_STATE.NEW,
              modified: gitState !== GIT_STATE.ORIGINAL,
              isBinary: localFile.isBinary,
              mimeType: localFile.mimeType
            });
            // Don't call updateUrlWithFile here - URL is already correct!
            // eslint-disable-next-line no-console
            console.log('✅ [RepositoryTree] Loaded file from URL on mount:', urlFilePath);
          } else {
            // eslint-disable-next-line no-console
            console.warn('⚠️ File from URL not found in IndexedDB:', urlFilePath);
          }
        } catch (error) {
          // eslint-disable-next-line no-console
          console.error('Failed to load file from URL:', error);
        }
      };
      
      loadFileFromUrl();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [miaProjectId, serviceName]); // Only run when project/service are loaded (mount)




  // Parse repository URL to extract project ID (supports GitLab, GitHub, Azure DevOps)
  useEffect(() => {
    // eslint-disable-next-line no-console
    console.log('🔍 Repository URL parsing effect triggered:', {
      repositoryUrl,
      hasUrl: !!repositoryUrl,
      urlType: typeof repositoryUrl
    });
    
    if (repositoryUrl) {
      try {
        // Detect provider
        const provider = unifiedGitDataService.detectProvider(repositoryUrl);
        let extractedProjectId;
        
        // eslint-disable-next-line no-console
        console.log(`🔍 Detected provider: ${provider} for URL:`, repositoryUrl);
        
        if (provider === 'azuredevops') {
          // Azure DevOps format: org/project/_git/repo
          // Or just the path directly: mia-platform-adevops/mia-demo/_git/hello-dotnet
          if (repositoryUrl.includes('://')) {
            const url = new URL(repositoryUrl);
            const pathParts = url.pathname.split('/').filter(Boolean);
            extractedProjectId = pathParts.join('/');
          } else {
            // Direct path format
            extractedProjectId = repositoryUrl;
          }
        } else {
          // GitLab/GitHub format: https://domain.com/namespace/project.git
          const url = new URL(repositoryUrl.replace('.git', ''));
          const pathParts = url.pathname.split('/').filter(Boolean);
          
          if (pathParts.length >= 2) {
            // Use the full path as project ID (GitLab supports nested namespaces)
            extractedProjectId = pathParts.join('/');
          }
        }
        
        if (extractedProjectId) {
          // eslint-disable-next-line no-console
          console.log(`✅ Extracted ${provider} project ID:`, extractedProjectId);
          setProjectId(extractedProjectId);
        } else {
          // eslint-disable-next-line no-console
          console.warn('⚠️ Could not extract project ID from repository URL:', repositoryUrl);
        }
      } catch (err) {
        // eslint-disable-next-line no-console
        console.error('Failed to parse repository URL:', err, 'URL:', repositoryUrl);
        setError('Invalid repository URL');
      }
    } else {
      // eslint-disable-next-line no-console
      console.warn('⚠️ Repository URL is not set yet');
    }
  }, [repositoryUrl]);

  // Fetch project info to get default branch (do NOT set ref from branchName until we try this first)
  useEffect(() => {
    const fetchProjectInfo = async () => {
      if (!projectId) {
        // eslint-disable-next-line no-console
        console.log('⏭️ Skipping project info fetch - projectId not set yet');
        return;
      }

      // Guard: verify that projectId (state) was actually derived from the current
      // repositoryUrl (prop). When the user switches resources the two effects fire
      // in separate React renders, so for one render cycle projectId still holds the
      // OLD value while repositoryUrl already has the NEW value → wrong provider +
      // wrong project_id combination → 404.
      // Re-derive the expected projectId from the current URL and skip if they don't match.
      if (repositoryUrl) {
        try {
          const provider = unifiedGitDataService.detectProvider(repositoryUrl);
          let expectedId;
          if (provider === 'azuredevops') {
            if (repositoryUrl.includes('://')) {
              const u = new URL(repositoryUrl);
              expectedId = u.pathname.split('/').filter(Boolean).join('/');
            } else {
              expectedId = repositoryUrl;
            }
          } else {
            const u = new URL(repositoryUrl.replace('.git', ''));
            expectedId = u.pathname.split('/').filter(Boolean).join('/');
          }
          if (expectedId && expectedId !== projectId) {
            // eslint-disable-next-line no-console
            console.log('⏭️ Skipping project info fetch - projectId/repositoryUrl not yet in sync', { projectId, expectedId });
            return;
          }
        } catch (_) {
          // If URL parsing fails here just let the call through; it will fail gracefully
        }
      }

      try {
        // eslint-disable-next-line no-console
        console.log('📡 Fetching project info to get default branch...');
        const info = await unifiedGitDataService.getProject(projectId, repositoryUrl);

        // Use default_branch if available, otherwise fall back to branchName prop or 'main'
        const defaultBranch = info.default_branch || branchName || 'main';
        setRef(defaultBranch);
        setRefType('branch'); // Initially it's always the default branch
        setProjectInfoFetched(true);
        
        // Save default branch to localStorage using utility
        if (miaProjectId && serviceName) {
          setGitRef(miaProjectId, serviceName, defaultBranch);
          // eslint-disable-next-line no-console
          console.log(`💾 Saved default branch to localStorage: ${defaultBranch}`);
        }

        // eslint-disable-next-line no-console
        console.log('📦 Project info fetched. Default branch:', defaultBranch);
      } catch (err) {
        // eslint-disable-next-line no-console
        console.error('Failed to fetch project info:', err);
        // Fall back to branchName prop or 'main' if we can't get project info
        const fallbackBranch = branchName || 'main';
        // eslint-disable-next-line no-console
        console.log('🌿 Using fallback branch after error:', fallbackBranch);
        setRef(fallbackBranch);
        setRefType('branch');
        setProjectInfoFetched(true);

        // Save fallback branch to localStorage using utility
        if (miaProjectId && serviceName) {
          setGitRef(miaProjectId, serviceName, fallbackBranch);
          // eslint-disable-next-line no-console
          console.log(`💾 Saved fallback branch to localStorage: ${fallbackBranch}`);
        }
      }
    };

    fetchProjectInfo();
  }, [projectId, repositoryUrl, branchName]);

  // Load root tree ONLY after project info is fetched and ref is set
  useEffect(() => {
    if (projectId && ref && projectInfoFetched) {
      // eslint-disable-next-line no-console
      console.log('🌳 Loading tree with branch:', ref);
      loadTree('', 0);
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [projectId, ref, projectInfoFetched]);

  // Refresh tree when switching between services - reload from IndexedDB
  useEffect(() => {
    if (miaProjectId && serviceName) {
      // eslint-disable-next-line no-console
      console.log('🔄 Service changed, refreshing RepositoryTree from IndexedDB:', serviceName);
      // Reset expanded folders and reload tree
      setExpandedFolders(new Set());
      setError(null);
      // Note: loadTree will handle missing projectId/ref gracefully
      // It will either load from IndexedDB or show appropriate error
      loadTree('', 0);
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [miaProjectId, serviceName]);

  // Fetch file states from IndexedDB
  useEffect(() => {
    const fetchFileStates = async () => {
      if (!miaProjectId || !serviceName) return;

      try {
        const allFiles = await indexedDbFileStorage.getAllFiles(miaProjectId, serviceName);
        const statesMap = {};
        
        allFiles.forEach(file => {
          // Use gitState (the new system) exclusively
          statesMap[file.filePath] = file.gitState || GIT_STATE.MODIFIED;
        });
        
        setFileStates(statesMap);
      } catch (error) {
        console.error('Error fetching file states:', error); /* eslint-disable-line no-console */
      }
    };

    fetchFileStates();

    // Refresh file states every 2 seconds to keep UI in sync
    const interval = setInterval(fetchFileStates, 2000);
    return () => clearInterval(interval);
  }, [miaProjectId, serviceName]);

  // Auto-expand folders to show selected file
  useEffect(() => {
    if (!selectedFilePath || !projectId || !ref) return;

    // Get all parent folders that need to be expanded
    const pathParts = selectedFilePath.split('/');
    const foldersToExpand = new Set(expandedFolders);
    
    // Build up the path incrementally
    let currentPath = '';
    for (let i = 0; i < pathParts.length - 1; i++) {
      currentPath += (currentPath ? '/' : '') + pathParts[i];
      foldersToExpand.add(currentPath);
    }
    
    // Only update if there are new folders to expand
    if (foldersToExpand.size > expandedFolders.size) {
      setExpandedFolders(foldersToExpand);
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedFilePath, projectId, ref]);

  // Scroll selected file into view
  useEffect(() => {
    if (!selectedFilePath) return;
    
    // Use setTimeout to ensure DOM has updated after folder expansion
    const timer = setTimeout(() => {
      const selectedElement = document.querySelector('.repository-tree-items .tree-item.selected');
      if (selectedElement) {
        selectedElement.scrollIntoView({ behavior: 'smooth', block: 'nearest' });
      }
    }, 100);
    
    return () => clearTimeout(timer);
  }, [selectedFilePath, expandedFolders]);

  // Close context menu when clicking outside
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (contextMenuRef.current && !contextMenuRef.current.contains(event.target)) {
        setContextMenu(null);
      }
    };

    if (contextMenu) {
      document.addEventListener('mousedown', handleClickOutside);
      return () => document.removeEventListener('mousedown', handleClickOutside);
    }
  }, [contextMenu]);

  // Listen for git push events to refresh the tree
  useEffect(() => {
    const handleGitPush = () => {
      // eslint-disable-next-line no-console
      console.log('🔄 Git Push completed - keeping tree visible, just clearing isNew flags...');
      // Don't reset the tree or expanded folders after push
      // The tree should remain visible with the same structure
      // Just reload to update file states (remove isNew badges from pushed files)
      if (miaProjectId && serviceName) {
        loadTree('', 0);
      }
    };

    const handleRepositoryFilesUpdated = (event) => {
      const {projectId: eventProjectId, serviceName: eventServiceName, fileCount} = event.detail || {};
      
      // Only refresh if it's for this project/service
      if (eventProjectId === miaProjectId && eventServiceName === serviceName) {
        // eslint-disable-next-line no-console
        console.log(`🌲 Repository files updated (${fileCount} files) - refreshing tree view`);
        // Reset expanded folders and reload tree from IndexedDB
        setExpandedFolders(new Set());
        loadTree('', 0);
      }
    };

    const handleGitPull = async (event) => {
      // Check if this pull request is for this specific service
      const {projectId: eventProjectId, serviceName: eventServiceName} = event.detail || {};
      
      // Only process pull requests for this specific service
      if (eventProjectId !== miaProjectId || eventServiceName !== serviceName) {
        // eslint-disable-next-line no-console
        console.log(`⏭️ Skipping pull request - not for this service (this: ${serviceName}, event: ${eventServiceName})`);
        return;
      }

      // ── PRE-FLIGHT: check tool enablement & authentication ──────────────────
      const provider = unifiedGitDataService.detectProvider(repositoryUrl);
      const providerName = provider === 'github' ? 'GitHub' : provider === 'azuredevops' ? 'Azure DevOps' : 'GitLab';

      // Use already-checked status if available, otherwise run the check now
      let statusToCheck = gitToolStatus;
      if (!statusToCheck.checked) {
        statusToCheck = await checkGitToolStatus(repositoryUrl);
        setGitToolStatus({ checked: true, ...statusToCheck });
      }

      if (statusToCheck.toolEnabled === false) {
        // eslint-disable-next-line no-console
        console.warn(`🚫 Cannot pull: ${providerName} tool is disabled on the server`);
        window.dispatchEvent(new CustomEvent('gitPullCompleted', {
          detail: { success: false, error: `${providerName} tool is disabled`, projectId: miaProjectId, serviceName }
        }));
        return;
      }

      if (statusToCheck.authenticated === false) {
        // eslint-disable-next-line no-console
        console.warn(`🔐 Cannot pull: not authenticated with ${providerName}`);
        const authErrorMsg = `Not authenticated with ${providerName}. Please connect your account in the Tools page.`;
        setError(authErrorMsg);
        window.dispatchEvent(new CustomEvent('gitPullCompleted', {
          detail: { success: false, error: authErrorMsg, projectId: miaProjectId, serviceName }
        }));
        return;
      }
      // ────────────────────────────────────────────────────────────────────────

      // Use branchName prop as fallback if ref is not set yet
      const effectiveRef = ref || branchName;
      
      if (!projectId || !effectiveRef || !miaProjectId || !serviceName) {
        // eslint-disable-next-line no-console
        console.error('⚠️ Cannot pull: missing required parameters', {
          projectId: projectId || 'MISSING',
          ref: ref || 'MISSING',
          branchName: branchName || 'MISSING',
          effectiveRef: effectiveRef || 'MISSING',
          miaProjectId: miaProjectId || 'MISSING',
          serviceName: serviceName || 'MISSING'
        });
        
        // Dispatch failure event
        window.dispatchEvent(new CustomEvent('gitPullCompleted', {
          detail: {
            success: false,
            error: `Missing required parameters`,
            projectId: miaProjectId,
            serviceName
          }
        }));
        return;
      }
      
      try {
        // Show loading state
        setGitLoading(true);
        isPullingRef.current = true; // Set flag to prevent concurrent pulls
        setGitLoadingMessage(`Connecting to ${providerName}...`);
        setGitProgress({ current: 0, total: 0 });
        setError(null);

        setGitLoadingMessage('Fetching repository structure...');
        // Fetch entire tree from Git (auth handled via cookies)
        const items = await unifiedGitDataService.getRepositoryTree(projectId, repositoryUrl, '', effectiveRef);

        const fileItems = items.filter(item => item.type === 'blob');
        const totalFiles = fileItems.length;
        
        // eslint-disable-next-line no-console
        console.log(`📥 Fetched ${totalFiles} files from ${providerName}`);

        setGitProgress({ current: 0, total: totalFiles });
        setGitLoadingMessage(`Downloading files (0/${totalFiles})...`);

        // Save all files to IndexedDB
        let currentFile = 0;
        for (const item of fileItems) {
          try {
            const fileData = await unifiedGitDataService.getFileContent(projectId, repositoryUrl, item.path, effectiveRef);

            // Detect if file is binary by extension (SVG is text-based, not binary)
            const ext = item.path.split('.').pop()?.toLowerCase();
            const binaryExtensions = ['png', 'jpg', 'jpeg', 'gif', 'bmp', 'ico', 'webp', 'tiff', 'pdf', 'zip', 'tar', 'gz'];
            const isBinary = binaryExtensions.includes(ext); // SVG explicitly excluded
            
            // For binary files, keep base64 content; for text files, use decoded content
            const contentToStore = isBinary ? fileData.content : (fileData.decodedContent || '');
            
            await indexedDbFileStorage.saveFile(miaProjectId, serviceName, item.path, contentToStore, {
              originalContent: contentToStore,
              fileName: item.name,
              branch: effectiveRef,
              isBinary,
              encoding: isBinary ? 'base64' : 'utf-8',
              fromGit: true // Force ORIGINAL state since this is pulled from GitLab
            });
            
            currentFile++;
            setGitProgress({ current: currentFile, total: totalFiles });
            setGitLoadingMessage(`Downloading files (${currentFile}/${totalFiles})...`);
            
            // eslint-disable-next-line no-console
            console.log(`💾 Saved to IndexedDB: ${item.path} (${currentFile}/${totalFiles})`);
          } catch (err) {
            // eslint-disable-next-line no-console
            console.error(`Failed to fetch ${item.path}:`, err);
            currentFile++;
            setGitProgress({ current: currentFile, total: totalFiles });
          }
        }

        // Remove local files that were deleted remotely (only ORIGINAL/PUSHED state - no local changes)
        const remoteFilePaths = new Set(fileItems.map(item => item.path));
        const localFiles = await indexedDbFileStorage.getAllFiles(miaProjectId, serviceName);
        for (const localFile of localFiles) {
          const isRemotelyDeleted = !remoteFilePaths.has(localFile.filePath);
          const hasNoLocalChanges = localFile.gitState === 'original' || localFile.gitState === 'pushed';
          if (isRemotelyDeleted && hasNoLocalChanges) {
            await indexedDbFileStorage.deleteFile(miaProjectId, serviceName, localFile.filePath, { suppressEvent: true });
            // eslint-disable-next-line no-console
            console.log(`🗑️ Removed remotely-deleted file from IndexedDB: ${localFile.filePath}`);
          }
        }

        setGitLoadingMessage('Loading files into tree view...');
        
        // eslint-disable-next-line no-console
        console.log('✅ Pull complete, reloading tree from IndexedDB');
        
        // Reset expanded folders and reload the tree from IndexedDB
        setExpandedFolders(new Set());
        await loadTree('', 0);

        // Dispatch event to notify other components (Monaco editor and preview will refresh automatically)
        window.dispatchEvent(new CustomEvent('gitPullCompleted', {
          detail: {
            success: true,
            projectId: miaProjectId,
            serviceName,
            branch: effectiveRef
          }
        }));
        
        // Hide loading state
        setGitLoading(false);
        isPullingRef.current = false; // Clear pulling flag
        setGitLoadingMessage('');
        setGitProgress({ current: 0, total: 0 });
        
      } catch (err) {
        const provider = unifiedGitDataService.detectProvider(repositoryUrl);
        const providerName = provider === 'github' ? 'GitHub' : provider === 'azuredevops' ? 'Azure DevOps' : 'GitLab';
        // eslint-disable-next-line no-console
        console.error(`Failed to pull from ${providerName}:`, err);
        
        // Check if it's an authentication error
        const isAuthError = err.message && err.message.includes('Not authenticated');
        const errorMessage = isAuthError 
          ? `Not authenticated with ${providerName}. Please connect your account in the Tools page.`
          : `Failed to pull: ${err.message}`;
        
        setError(errorMessage);
        
        isPullingRef.current = false; // Clear pulling flag even on error
        // Dispatch failure event
        window.dispatchEvent(new CustomEvent('gitPullCompleted', {
          detail: {
            success: false,
            error: err.message,
            projectId: miaProjectId,
            serviceName,
            branch: effectiveRef
          }
        }));
        
        setGitLoading(false);
        setGitLoadingMessage('');
        setGitProgress({ current: 0, total: 0 });
      }
    };

    const handleGitPushRequest = async (event) => {
      const {projectId: eventProjectId, serviceName: eventServiceName, files} = event.detail || {};
      
      // Only handle if this event is for this specific service
      if (miaProjectId !== eventProjectId || serviceName !== eventServiceName) {
        return;
      }
      
      // eslint-disable-next-line no-console
      console.log(`🚀 Git Push: Pushing ${files?.length || 0} file(s) to GitLab...`);
      
      if (!projectId || !ref || !miaProjectId || !serviceName) {
        // eslint-disable-next-line no-console
        console.warn('⚠️ Cannot push: missing required parameters');
        window.dispatchEvent(new CustomEvent('gitPushCompleted', {
          detail: {
            success: false,
            error: 'Missing required parameters'
          }
        }));
        return;
      }
      
      try {
        // Show loading state
        setGitLoading(true);
        setGitLoadingMessage('Preparing to push changes...');
        setGitProgress({ current: 0, total: 0 });
        setError(null);

        const provider = unifiedGitDataService.detectProvider(repositoryUrl);
        const providerName = provider === 'github' ? 'GitHub' : provider === 'azuredevops' ? 'Azure DevOps' : 'GitLab';

        // Get modified and deleted files from IndexedDB
        setGitLoadingMessage('Collecting modified files...');
        const modifiedFilesData = await indexedDbFileStorage.getModifiedFiles(miaProjectId, serviceName);
        const deletedFilesData = await indexedDbFileStorage.getDeletedFiles(miaProjectId, serviceName);
        
        // eslint-disable-next-line no-console
        console.log('📊 Files retrieved from IndexedDB:', {
          modifiedCount: modifiedFilesData.length,
          deletedCount: deletedFilesData.length,
          modifiedFiles: modifiedFilesData.map(f => ({path: f.filePath, state: f.state, contentLength: f.content?.length})),
          deletedFiles: deletedFilesData.map(f => ({path: f.filePath, state: f.state}))
        });
        
        const modifiedFiles = modifiedFilesData.map(f => ({
          path: f.filePath,
          content: f.content
        }));
        const deletedFiles = deletedFilesData.map(f => f.filePath);

        if (modifiedFiles.length === 0 && deletedFiles.length === 0) {
          // eslint-disable-next-line no-console
          console.log('⚠️ No modified or deleted files to push');
          setGitLoading(false);
          setGitLoadingMessage('');
          setGitProgress({ current: 0, total: 0 });
          setGitPushFiles([]);
          window.dispatchEvent(new CustomEvent('gitPushCompleted', {
            detail: {
              success: true,
              filesCount: 0
            }
          }));
          return;
        }

        // Set total for progress tracking
        const totalFiles = modifiedFiles.length + deletedFiles.length;
        setGitProgress({ current: 0, total: totalFiles });
        setGitLoadingMessage(`Preparing ${totalFiles} file(s) for push...`);

        // STEP 1: Pull latest changes from GitLab to sync with remote
        setGitLoadingMessage('🔄 Pulling latest changes from GitLab...');
        // eslint-disable-next-line no-console
        console.log('🔄 Step 1: Pulling latest changes from GitLab before push...');
        
        const gitlabTree = await unifiedGitDataService.getRepositoryTree(projectId, repositoryUrl, '', ref);
        const existingFilesSet = new Set(gitlabTree.filter(item => item.type === 'blob').map(item => item.path));
        
        // eslint-disable-next-line no-console
        console.log(`📋 GitLab has ${existingFilesSet.size} existing files`);
        
        // STEP 2: Fetch content of modified files from GitLab to check for conflicts
        setGitLoadingMessage('🔍 Checking for conflicts...');
        const conflicts = [];
        
        for (const file of modifiedFiles) {
          if (existingFilesSet.has(file.path)) {
            try {
              const gitlabFile = await unifiedGitDataService.getFileContent(projectId, repositoryUrl, file.path, ref);
              const localFile = modifiedFilesData.find(f => f.filePath === file.path);
              
              // Check if GitLab version differs from our original version
              if (localFile?.originalContent && gitlabFile.decodedContent !== localFile.originalContent) {
                conflicts.push({
                  path: file.path,
                  localContent: file.content,
                  remoteContent: gitlabFile.decodedContent,
                  originalContent: localFile.originalContent
                });
              }
            } catch (err) {
              // File might have been deleted remotely, that's ok
              // eslint-disable-next-line no-console
              console.log(`ℹ️ Could not fetch ${file.path} from GitLab (might be deleted):`, err.message);
            }
          }
        }
        
        if (conflicts.length > 0) {
          // eslint-disable-next-line no-console
          console.warn(`⚠️ Found ${conflicts.length} file(s) with conflicts:`, conflicts.map(c => c.path));
          
          // For now, use local version (can be enhanced with merge UI later)
          // eslint-disable-next-line no-console
          console.log('📝 Using local version for conflicted files (remote changes will be overwritten)');
        }
        
        // eslint-disable-next-line no-console
        console.log('✅ Conflict check complete, proceeding with push...');
        
        // eslint-disable-next-line no-console
        console.log(`📋 GitLab has ${existingFilesSet.size} existing files`);

        // Determine correct action based on whether file exists in GitLab
        setGitLoadingMessage('Preparing file actions...');
        
        // Track all file paths to detect duplicates
        const processedPaths = new Set();
        const fileActions = [];
        
        for (const file of modifiedFiles) {
          if (file.content === undefined || file.content === null) continue;
          if (processedPaths.has(file.path)) {
            // eslint-disable-next-line no-console
            console.warn(`⚠️ Skipping duplicate file: ${file.path}`);
            continue;
          }
          
          processedPaths.add(file.path);
          
          // Use correct action: 'create' for new files, 'update' for existing files
          const existsInGitLab = existingFilesSet.has(file.path);
          const action = existsInGitLab ? 'update' : 'create';
          const fileData = modifiedFilesData.find(f => f.filePath === file.path);
          const isBinary = fileData?.isBinary || false;
          
          // eslint-disable-next-line no-console
          console.log(`📝 ${action.toUpperCase()} action for ${file.path} (binary: ${isBinary}, existsInGitLab: ${existsInGitLab})`);
          
          const actionObj = {
            action,
            file_path: file.path,
            content: file.content
          };
          
          // For binary files, specify base64 encoding
          if (isBinary) {
            actionObj.encoding = 'base64';
          }
          
          fileActions.push(actionObj);
        }

        const deleteActions = deletedFiles
          .filter(filePath => {
            if (processedPaths.has(filePath)) {
              // eslint-disable-next-line no-console
              console.warn(`⚠️ Skipping delete for file already processed: ${filePath}`);
              return false;
            }
            if (!existingFilesSet.has(filePath)) {
              // eslint-disable-next-line no-console
              console.warn(`⚠️ Skipping delete for file that doesn't exist in GitLab: ${filePath}`);
              return false;
            }
            processedPaths.add(filePath);
            return true;
          })
          .map(filePath => ({
            action: 'delete',
            file_path: filePath
          }));

        const actions = [...fileActions, ...deleteActions];
        
        // Build file list for UI display
        const fileListForDisplay = [
          ...modifiedFiles.map(f => ({
            path: f.path,
            action: existingFilesSet.has(f.path) ? 'update' : 'create',
            status: 'pending'
          })),
          ...deletedFiles.map(f => ({
            path: f,
            action: 'delete',
            status: 'pending'
          }))
        ];
        setGitPushFiles(fileListForDisplay);
        
        if (actions.length === 0) {
          // eslint-disable-next-line no-console
          console.log('⚠️ No valid changes to push');
          setGitLoading(false);
          setGitLoadingMessage('');
          setGitProgress({ current: 0, total: 0 });
          setGitPushFiles([]);
          window.dispatchEvent(new CustomEvent('gitPushCompleted', {
            detail: {
              success: true,
              filesCount: 0
            }
          }));
          return;
        }
        
        // eslint-disable-next-line no-console
        console.log('📤 Pushing to GitLab:', {
          totalActions: actions.length,
          creates: actions.filter(a => a.action === 'create').length,
          updates: actions.filter(a => a.action === 'update').length,
          deletes: actions.filter(a => a.action === 'delete').length
        });

        // Show which files are being pushed
        const createCount = actions.filter(a => a.action === 'create').length;
        const updateCount = actions.filter(a => a.action === 'update').length;
        const deleteCount = actions.filter(a => a.action === 'delete').length;
        setGitLoadingMessage(`Pushing ${createCount} new, ${updateCount} updated, ${deleteCount} deleted file(s)...`);

        // Get accumulated commit messages from IndexedDB
        let commitMessage;
        try {
          const tenantId = getTenantId();
          const accumulatedMessage = await commitMessageStorage.getMessage(tenantId, miaProjectId, serviceName);
          
          if (accumulatedMessage) {
            // Use accumulated commit messages
            commitMessage = accumulatedMessage;
            // eslint-disable-next-line no-console
            console.log('📝 Using accumulated commit messages (length:', commitMessage.length, 'chars)');
          } else {
            // Fallback to default commit message
            const timestamp = new Date().toISOString();
            commitMessage = `Update ${modifiedFiles.length + deletedFiles.length} file(s) via Mia Flow\n\n${modifiedFiles.length > 0 ? `Modified files:\n${modifiedFiles.map(f => `- ${f.path}`).join('\n')}\n` : ''}${deletedFiles.length > 0 ? `Deleted files:\n${deletedFiles.map(f => `- ${f}`).join('\n')}\n` : ''}\nCommitted at: ${timestamp}`;
            // eslint-disable-next-line no-console
            console.log('📝 Using default commit message');
          }
        } catch (error) {
          // eslint-disable-next-line no-console
          console.error('Failed to get commit messages, using default:', error);
          const timestamp = new Date().toISOString();
          commitMessage = `Update ${modifiedFiles.length + deletedFiles.length} file(s) via Mia Flow\n\n${modifiedFiles.length > 0 ? `Modified files:\n${modifiedFiles.map(f => `- ${f.path}`).join('\n')}\n` : ''}${deletedFiles.length > 0 ? `Deleted files:\n${deletedFiles.map(f => `- ${f}`).join('\n')}\n` : ''}\nCommitted at: ${timestamp}`;
        }
        
        // GitLab has a limit on commit message size (typically 1MB for API, but let's be conservative)
        // Truncate if too long to avoid 400 Bad Request errors
        const MAX_COMMIT_MESSAGE_LENGTH = 2000; // 2KB limit - GitLab can be picky about long messages
        if (commitMessage.length > MAX_COMMIT_MESSAGE_LENGTH) {
          const originalLength = commitMessage.length;
          const filesSummary = `\n\nUpdated ${modifiedFiles.length} file(s) and deleted ${deletedFiles.length} file(s).`;
          commitMessage = commitMessage.substring(0, MAX_COMMIT_MESSAGE_LENGTH - filesSummary.length - 50) + '\n\n... (message truncated)' + filesSummary;
          // eslint-disable-next-line no-console
          console.warn(`⚠️ Commit message truncated from ${originalLength} to ${commitMessage.length} chars`);
        }

        // Commit all changes in a single commit
        await unifiedGitDataService.commitChanges(
          projectId,
          repositoryUrl,
          ref,
          commitMessage,
          actions
        );
        
        // Update progress as we process files
        setGitLoadingMessage('Updating file states...');
        let processedCount = 0;
        
        // Mark modified files as sent and delete deleted files from IndexedDB
        for (const file of modifiedFiles) {
          await indexedDbFileStorage.markAsSent(miaProjectId, serviceName, file.path);
          processedCount++;
          setGitProgress({ current: processedCount, total: totalFiles });
          setGitLoadingMessage(`Updated ${processedCount}/${totalFiles}: ${file.path}`);
          
          // Update file status in the list
          setGitPushFiles(prev => prev.map(f => 
            f.path === file.path ? {...f, status: 'completed'} : f
          ));
        }
        for (const filePath of deletedFiles) {
          await indexedDbFileStorage.deleteFile(miaProjectId, serviceName, filePath);
          processedCount++;
          setGitProgress({ current: processedCount, total: totalFiles });
          setGitLoadingMessage(`Updated ${processedCount}/${totalFiles}: ${filePath}`);
          
          // Update file status in the list
          setGitPushFiles(prev => prev.map(f => 
            f.path === filePath ? {...f, status: 'completed'} : f
          ));
        }

        // eslint-disable-next-line no-console
        console.log('✅ Push complete, reloading tree from IndexedDB');
        
        // Clear accumulated commit messages after successful push
        try {
          const tenantId = getTenantId();
          await commitMessageStorage.clearMessage(tenantId, miaProjectId, serviceName);
          // eslint-disable-next-line no-console
          console.log('🧹 Cleared accumulated commit messages after successful push');
        } catch (clearError) {
          // eslint-disable-next-line no-console
          console.warn('⚠️ Failed to clear commit messages:', clearError);
        }
        
        setGitLoadingMessage('Refreshing file tree...');
        
        // Reload the tree to update file states
        await loadTree('', 0);

        // Hide loading state
        setGitLoading(false);
        setGitLoadingMessage('');
        setGitProgress({ current: 0, total: 0 });
        setGitPushFiles([]);

        // Dispatch event to notify other components
        window.dispatchEvent(new CustomEvent('gitPushCompleted', {
          detail: {
            success: true,
            projectId: miaProjectId,
            serviceName,
            branch: ref,
            filesCount: modifiedFiles.length + deletedFiles.length
          }
        }));
        
      } catch (err) {
        // eslint-disable-next-line no-console
        console.error('Failed to push to GitLab:', err);
        
        // Hide loading state on error
        setGitLoading(false);
        setGitLoadingMessage('');
        setGitProgress({ current: 0, total: 0 });
        setGitPushFiles([]);
        setError(`Failed to push: ${err.message}`);
        
        // Dispatch error event
        window.dispatchEvent(new CustomEvent('gitPushCompleted', {
          detail: {
            success: false,
            error: err.message
          }
        }));
      }
    };

    const handleFileOperation = async (event) => {
      const {operation, path, content} = event.detail;
      
      if (operation === 'create' && path && content !== undefined) {
        // eslint-disable-next-line no-console
        console.log('✨ AI is creating new file in tree:', path);
        
        // Extract filename
        const pathParts = path.split('/');
        const fileName = pathParts[pathParts.length - 1];
        
        // Create the new file object
        const newFile = {
          path,
          name: fileName,
          content,
          type: 'blob',
          isNew: true
        };
        
        // Save to IndexedDB
        if (miaProjectId && serviceName) {
          await indexedDbFileStorage.saveFile(miaProjectId, serviceName, path, content, {
            originalContent: '',
            fileName,
            isNew: true
          });
          
          // eslint-disable-line no-console
          console.log('💾 Saved new file to IndexedDB:', path);
        }
        
        // Automatically expand all parent folders and update tree incrementally
        // This preserves the existing expanded state while adding the new file
        setExpandedFolders(prev => {
          const next = new Set(prev);
          const parts = path.split('/');
          let currentPath = '';
          
          // Loop through all folders in the path (excluding the file itself)
          for (let i = 0; i < parts.length - 1; i++) {
            currentPath = currentPath ? `${currentPath}/${parts[i]}` : parts[i];
            next.add(currentPath);
          }
          return next;
        });
        
        // Instead of reloading the entire tree, add the new file and folders incrementally
        setTree(prevTree => {
          const newTree = [...prevTree];
          const newItems = [];
          const parentPath = pathParts.slice(0, -1).join('/');
          const fileLevel = pathParts.length - 1;
          
          // Check if file already exists
          const fileExists = newTree.some(item => item.path === path);
          
          if (!fileExists) {
            // Add any missing parent folders
            const foldersToAdd = [];
            for (let i = 1; i < pathParts.length; i++) {
              const folderPath = pathParts.slice(0, i).join('/');
              const folderExists = newTree.some(item => item.path === folderPath);
              
              if (!folderExists) {
                const folderParts = folderPath.split('/');
                const folderName = folderParts[folderParts.length - 1];
                const folderLevel = folderParts.length - 1;
                const folderParentPath = folderParts.slice(0, -1).join('/');
                
                foldersToAdd.push({
                  path: folderPath,
                  name: folderName,
                  type: 'tree',
                  level: folderLevel,
                  parent: folderParentPath || undefined
                });
              }
            }
            
            // Add the new file
            newItems.push({
              path,
              name: fileName,
              type: 'blob',
              level: fileLevel,
              parent: parentPath || undefined,
              isNew: true,
              modified: false
            });
            
            // Combine and sort
            const combinedTree = [...newTree, ...foldersToAdd, ...newItems];
            
            // Sort using the same logic as loadTree
            combinedTree.sort((a, b) => {
              const aPath = a.path.split('/');
              const bPath = b.path.split('/');
              const minLength = Math.min(aPath.length, bPath.length);
              
              for (let i = 0; i < minLength; i++) {
                if (aPath[i] !== bPath[i]) {
                  const aIsFolder = i < aPath.length - 1 || a.type === 'tree';
                  const bIsFolder = i < bPath.length - 1 || b.type === 'tree';
                  
                  if (aIsFolder && !bIsFolder) return -1;
                  if (!aIsFolder && bIsFolder) return 1;
                  
                  return aPath[i].localeCompare(bPath[i], undefined, { numeric: true, sensitivity: 'base' });
                }
              }
              
              if (aPath.length !== bPath.length) {
                return aPath.length - bPath.length;
              }
              
              if (a.type !== b.type) {
                return a.type === 'tree' ? -1 : 1;
              }
              
              return 0;
            });
            
            return combinedTree;
          }
          
          return prevTree;
        });
        
        // Auto-open the file in the editor
        onFileSelect(newFile);
        
        // Dispatch file content changed event to trigger preview update
        window.dispatchEvent(new CustomEvent('fileContentChanged', {
          detail: {
            filePath: path,
            content,
            projectId: miaProjectId,
            serviceName
          }
        }));
        
        // eslint-disable-next-line no-console
        console.log('✅ File added to tree and opened in editor:', path);
      } else if (operation === 'delete' && path) {
        // eslint-disable-next-line no-console
        console.log('🗑️ AI is deleting file from tree:', path);
        
        // Mark as deleted in IndexedDB
        if (miaProjectId && serviceName) {
          await indexedDbFileStorage.markAsDeleted(miaProjectId, serviceName, path);
        }
        
        // Remove the file from tree without reloading everything
        setTree(prevTree => prevTree.filter(item => item.path !== path));
      }
    };

    // Handler for when git pull completes (reload tree only if successful)
    const handleGitPullCompleted = (event) => {
      const {success, projectId: eventProjectId, serviceName: eventServiceName} = event?.detail || {};
      
      // Only reload tree if this event is for this service and pull was successful
      if (eventProjectId === miaProjectId && eventServiceName === serviceName && success) {
        loadTree('', 0);
      }
    };

    // Listen for auto-trigger pull events from Canvas
    const handleAutoTriggerGitPull = async (event) => {
      const {projectId: eventProjectId, serviceName: eventServiceName, reason} = event.detail || {};
      
      // Only trigger if this event is for this specific service
      if (miaProjectId === eventProjectId && serviceName === eventServiceName) {
        // eslint-disable-next-line no-console
        console.log(`🤖 Auto-triggering Git Pull (reason: ${reason})...`);
        
        // Call the same handleGitPull function with the event
        await handleGitPull(event);
      }
    };

    // Listen for git ref (branch/tag) change events
    const handleGitRefChanged = async (event) => {
      const {projectId: eventProjectId, serviceName: eventServiceName, ref: newRef, refType: newRefType} = event.detail || {};
      
      // Only trigger if this event is for this specific service
      if (miaProjectId === eventProjectId && serviceName === eventServiceName) {
        // eslint-disable-next-line no-console
        console.log(`🔄 Git ref changed to ${newRefType}: ${newRef}, reloading files...`);
        
        // Update the ref state and refType
        setRef(newRef);
        setRefType(newRefType || 'branch'); // Default to 'branch' if not specified
        
        // Clear the tree and reload from GitLab with the event
        await handleGitPull(event);
      }
    };

    // Handler for opening files programmatically (from modified files list, search, etc.)
    const handleOpenFileEvent = async (event) => {
      const {filePath, projectId: eventProjectId, serviceName: eventServiceName, lineNumber} = event.detail || {};
      
      // Debug logging
      // eslint-disable-next-line no-console
      console.log('🔍 handleOpenFileEvent received:', {filePath, lineNumber, eventProjectId, eventServiceName});
      
      // Only handle if this event is for this specific service (or no specific service provided)
      if (eventProjectId && eventServiceName && (miaProjectId !== eventProjectId || serviceName !== eventServiceName)) {
        return;
      }
      
      if (!filePath) {
        console.warn('⚠️ openFile event missing filePath'); /* eslint-disable-line no-console */
        return;
      }
      
      // eslint-disable-next-line no-console
      console.log('📂 Opening file from programmatic request:', filePath, lineNumber ? `line ${lineNumber}` : '');
      
      // Find the item in the tree
      const findItemInTree = (items, targetPath) => {
        for (const item of items) {
          if (item.path === targetPath) return item;
          if (item.children) {
            const found = findItemInTree(item.children, targetPath);
            if (found) return found;
          }
        }
        return null;
      };
      
      const item = findItemInTree(tree, filePath);
      
      if (item) {
        // Use the existing handleFileClick function to open the file
        await handleFileClick(item, lineNumber);
      } else {
        // File not in tree yet - try to load from IndexedDB and open
        if (miaProjectId && serviceName) {
          const fileData = await indexedDbFileStorage.getFile(miaProjectId, serviceName, filePath);
          if (fileData) {
            // eslint-disable-next-line no-console
            console.log('📂 File found in IndexedDB, opening:', filePath);
            const gitState = fileData.gitState || GIT_STATE.MODIFIED;
            onFileSelect({
              path: filePath,
              name: filePath.split('/').pop(),
              content: fileData.content,
              isNew: gitState === GIT_STATE.NEW,
              modified: gitState !== GIT_STATE.ORIGINAL,
              lineNumber
            });
          } else {
            console.warn('⚠️ File not found in tree or IndexedDB:', filePath); /* eslint-disable-line no-console */
          }
        }
      }
    };

    window.addEventListener('gitPushCompleted', handleGitPush);
    window.addEventListener('gitPushRequested', handleGitPushRequest);
    window.addEventListener('repositoryFilesUpdated', handleRepositoryFilesUpdated);
    window.addEventListener('gitPullRequested', handleGitPull);
    window.addEventListener('gitPullCompleted', handleGitPullCompleted);
    window.addEventListener('fileOperation', handleFileOperation);
    window.addEventListener('autoTriggerGitPull', handleAutoTriggerGitPull);
    window.addEventListener('gitRefChanged', handleGitRefChanged);
    window.addEventListener('openFile', handleOpenFileEvent);
    window.addEventListener('openFileFromSearch', handleOpenFileEvent);

    return () => {
      window.removeEventListener('gitPushCompleted', handleGitPush);
      window.removeEventListener('gitPushRequested', handleGitPushRequest);
      window.removeEventListener('repositoryFilesUpdated', handleRepositoryFilesUpdated);
      window.removeEventListener('gitPullRequested', handleGitPull);
      window.removeEventListener('gitPullCompleted', handleGitPullCompleted);
      window.removeEventListener('fileOperation', handleFileOperation);
      window.removeEventListener('autoTriggerGitPull', handleAutoTriggerGitPull);
      window.removeEventListener('gitRefChanged', handleGitRefChanged);
      window.removeEventListener('openFile', handleOpenFileEvent);
      window.removeEventListener('openFileFromSearch', handleOpenFileEvent);
    };
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [projectId, ref, miaProjectId, serviceName, onFileSelect]);

  const loadTree = async (path, level) => {
    try {
      setLoading(true);
      setError(null);

      // Dispatch loading event for top loading bar
      window.dispatchEvent(new CustomEvent('indexedDBLoading', {
        detail: {loading: true, message: 'Loading file tree from IndexedDB...'}
      }));

      // eslint-disable-next-line no-console
      console.log(`🌳 loadTree called - path: "${path}", level: ${level}`);

      if (path === '') {
        // Root level - load from localStorage ONLY
        // GitLab fetch should ONLY happen via explicit Pull button
        
        if (!miaProjectId || !serviceName) {
          // eslint-disable-next-line no-console
          console.warn('⚠️ Cannot load tree: missing projectId or serviceName');
          setTree([]);
          return;
        }

        // Check if we have ANY files in IndexedDB
        const modifiedFiles = await indexedDbFileStorage.getModifiedFiles(miaProjectId, serviceName);
        const allFilesData = await indexedDbFileStorage.getAllFiles(miaProjectId, serviceName);
        const allFiles = allFilesData.map(f => f.filePath);
        
        // eslint-disable-next-line no-console
        console.log(`📦 Loading tree from IndexedDB: ${allFiles.length} total files, ${modifiedFiles.length} modified`);
        
        if (allFiles.length === 0) {
          // No files in IndexedDB - check if we can trigger automatic Pull
          // Use branchName prop as fallback if ref is not set yet
          const effectiveRef = ref || branchName;
          
          if (!projectId || !effectiveRef || !miaProjectId || !serviceName) {
            // Missing parameters - if projectId is not set yet, it's likely still being extracted
            // from the repository URL, so don't spam warnings
            if (!projectId && repositoryUrl) {
              // eslint-disable-next-line no-console
              console.log('⏸️ Waiting for project ID to be extracted from repository URL...');
            } else {
              // eslint-disable-next-line no-console
              console.warn('⚠️ Cannot auto-pull: missing required parameters', {
                projectId: projectId || 'MISSING',
                ref: ref || 'MISSING',
                branchName: branchName || 'MISSING',
                effectiveRef: effectiveRef || 'MISSING',
                miaProjectId: miaProjectId || 'MISSING',
                serviceName: serviceName || 'MISSING',
                repositoryUrl: repositoryUrl || 'MISSING'
              });
            }
            setTree([]);
            window.dispatchEvent(new CustomEvent('indexedDBLoading', {
              detail: {loading: false}
            }));
            return;
          }
          
          // Check if authenticated before auto-pulling
          const provider = unifiedGitDataService.detectProvider(repositoryUrl);
          const providerName = provider === 'github' ? 'GitHub' : provider === 'azuredevops' ? 'Azure DevOps' : 'GitLab';

          // Check if already pulling to prevent loops
          if (isPullingRef.current) {
            // eslint-disable-next-line no-console
            console.log('⏭️ Skipping auto-pull - already pulling in progress');
            window.dispatchEvent(new CustomEvent('indexedDBLoading', {
              detail: {loading: false}
            }));
            return;
          }
          
          // eslint-disable-next-line no-console
          console.log(`⚠️ No files in IndexedDB. Automatically pulling from ${providerName}...`);
          
          // Dispatch loading complete event before triggering pull
          window.dispatchEvent(new CustomEvent('indexedDBLoading', {
            detail: {loading: false}
          }));
          
          // Automatically trigger pull via event (since handleGitPull is in a different scope)
          setTree([]);
          window.dispatchEvent(new CustomEvent('gitPullRequested'));
          return;
        }

        // Build tree from localStorage
        const treeItems = [];
        const folders = new Set();

        // Collect all folders from file paths
        for (const filePath of allFiles) {
          const fileData = await indexedDbFileStorage.getFile(miaProjectId, serviceName, filePath);
          
          // Skip deleted files
          const gitState = fileData?.gitState || GIT_STATE.MODIFIED;
          if (fileData && gitState === GIT_STATE.DELETED) continue;

          const pathParts = filePath.split('/');
          
          // Create all parent folders
          for (let i = 1; i < pathParts.length; i++) {
            const folderPath = pathParts.slice(0, i).join('/');
            folders.add(folderPath);
          }
          
          // Add the file
          const fileName = pathParts[pathParts.length - 1];
          const parentPath = pathParts.slice(0, -1).join('/');
          const fileLevel = pathParts.length - 1;
          
          treeItems.push({
            path: filePath,
            name: fileName,
            type: 'blob',
            level: fileLevel,
            parent: parentPath || undefined,
            isNew: gitState === GIT_STATE.NEW,
            modified: gitState === GIT_STATE.MODIFIED,
            deleted: gitState === GIT_STATE.DELETED
          });
        }

        // Add folders
        for (const folderPath of folders) {
          const folderParts = folderPath.split('/');
          const folderName = folderParts[folderParts.length - 1];
          const folderLevel = folderParts.length - 1;
          const parentPath = folderParts.slice(0, -1).join('/');
          
          treeItems.push({
            path: folderPath,
            name: folderName,
            type: 'tree',
            level: folderLevel,
            parent: parentPath || undefined
          });
        }

        // Sort items: hierarchical tree order with folders first, then files, all alphabetically
        treeItems.sort((a, b) => {
          // Split paths to compare hierarchically
          const aPath = a.path.split('/');
          const bPath = b.path.split('/');
          
          // Compare each level of the path
          const minLength = Math.min(aPath.length, bPath.length);
          
          for (let i = 0; i < minLength; i++) {
            // If we're at different folders at this level, sort alphabetically
            if (aPath[i] !== bPath[i]) {
              // Check if both items at this level exist in our tree
              const aIsFolder = i < aPath.length - 1 || a.type === 'tree';
              const bIsFolder = i < bPath.length - 1 || b.type === 'tree';
              
              // If one is a folder and one is a file at this level, folder comes first
              if (aIsFolder && !bIsFolder) return -1;
              if (!aIsFolder && bIsFolder) return 1;
              
              // Both same type at this level, sort alphabetically
              return aPath[i].localeCompare(bPath[i], undefined, { numeric: true, sensitivity: 'base' });
            }
          }
          
          // One path is a prefix of the other
          // Shorter path (parent) comes before longer path (child)
          if (aPath.length !== bPath.length) {
            return aPath.length - bPath.length;
          }
          
          // Same length paths - folders before files
          if (a.type !== b.type) {
            return a.type === 'tree' ? -1 : 1;
          }
          
          return 0;
        });

        // eslint-disable-next-line no-console
        console.log(`✅ Loaded ${treeItems.length} items from IndexedDB`);
        
        setTree(treeItems);
      }
    } catch (err) {
      // eslint-disable-next-line no-console
      console.error('Error loading repository tree:', err);
      setError(err.message || 'Failed to load repository');
    } finally {
      setLoading(false);
      // Clear loading event
      window.dispatchEvent(new CustomEvent('indexedDBLoading', {
        detail: {loading: false, message: ''}
      }));
    }
  };

  const handleFolderToggle = async (item) => {
    const newExpanded = new Set(expandedFolders);
    
    if (expandedFolders.has(item.path)) {
      // Collapse: just remove from expanded set
      newExpanded.delete(item.path);
    } else {
      // Expand: just add to expanded set
      // All items are already in the tree from initial load
      newExpanded.add(item.path);
    }
    
    setExpandedFolders(newExpanded);
  };

  const handleFileClick = async (item, lineNumber = null) => {
    if (item.type === 'tree') {
      await handleFolderToggle(item);
    } else {
      try {
        setLoading(true);
        
        // Check IndexedDB first for new or modified files
        if (miaProjectId && serviceName) {
          const localFile = await indexedDbFileStorage.getFile(miaProjectId, serviceName, item.path);
          
          // Check if it's a binary file
          if (localFile?.isBinary) {
            // For binary files, create a download/view experience
            // eslint-disable-next-line no-console
            console.log('📂 Binary file detected:', item.path, `(${localFile.mimeType || 'unknown type'})`);
            
            // Create blob URL for viewing/downloading
            const url = await indexedDbFileStorage.getFileAsUrl(miaProjectId, serviceName, item.path);
            
            if (url) {
              // For images, we can show in editor with special handling
              if (localFile.mimeType?.startsWith('image/')) {
                const gitState = localFile.gitState || GIT_STATE.MODIFIED;
                onFileSelect({
                  path: item.path,
                  name: item.name,
                  content: `[Binary Image File: ${item.name}]\n\nMIME Type: ${localFile.mimeType}\nSize: ${formatFileSize(localFile.size || 0)}\n\nPreview URL: ${url}`,
                  isNew: gitState === GIT_STATE.NEW,
                  modified: gitState !== GIT_STATE.ORIGINAL,
                  isBinary: true,
                  binaryUrl: url,
                  mimeType: localFile.mimeType,
                  lineNumber
                });
                updateUrlWithFile(item.path); // Update URL
              } else {
                // For other binary files (PDFs, etc.), show info and download link
                const gitState = localFile.gitState || GIT_STATE.MODIFIED;
                onFileSelect({
                  path: item.path,
                  name: item.name,
                  content: `[Binary File: ${item.name}]\n\nMIME Type: ${localFile.mimeType || 'application/octet-stream'}\nSize: ${formatFileSize(localFile.size || 0)}\n\nThis file cannot be edited as text.\nDownload URL: ${url}`,
                  isNew: gitState === GIT_STATE.NEW,
                  modified: gitState !== GIT_STATE.ORIGINAL,
                  isBinary: true,
                  binaryUrl: url,
                  mimeType: localFile.mimeType,
                  lineNumber
                });
              }
              setLoading(false);
              return;
            }
          }
          
          // Use IndexedDB if file is new OR has modifications
          const gitState = localFile?.gitState || GIT_STATE.MODIFIED;
          if (localFile && (gitState !== GIT_STATE.ORIGINAL || localFile.content !== undefined)) {
            // Log content type for debugging
            const contentType = typeof localFile.content;
            const contentPreview = localFile.content ? 
              (typeof localFile.content === 'string' ? localFile.content.substring(0, 100) : `[${contentType}]`) : 
              'undefined';
            
            // eslint-disable-next-line no-console
            console.log('📂 Loading file from IndexedDB:', item.path, `(${gitState})`, `type: ${contentType}`, `preview: ${contentPreview}`);
            
            // Validate localStorage content before using it
            const contentLooksInvalid = localFile.content && 
              typeof localFile.content === 'string' &&
              localFile.content.includes('npm install') && 
              localFile.content.includes('yarn add') && 
              !localFile.content.includes('import') &&
              !item.path.endsWith('.md') &&
              !item.path.endsWith('.json');
            
            if (contentLooksInvalid) {
              // eslint-disable-next-line no-console
              console.error('❌ RepositoryTree: Corrupted content in IndexedDB for', item.path);
              // eslint-disable-next-line no-console
              console.error('   Deleting corrupted entry and loading from GitLab instead');
              await indexedDbFileStorage.deleteFile(miaProjectId, serviceName, item.path);
              // Fall through to load from GitLab
            } else {
              // eslint-disable-next-line no-console
              console.log('📂 Loading file from IndexedDB:', item.path, `(${gitState})`);
              
              onFileSelect({
                path: item.path,
                name: item.name,
                content: localFile.content,
                isNew: gitState === GIT_STATE.NEW,
                modified: gitState !== GIT_STATE.ORIGINAL,
                isBinary: localFile.isBinary,
                mimeType: localFile.mimeType,
                lineNumber
              });
              updateUrlWithFile(item.path); // Update URL
              setLoading(false);
              return;
            }
          }
        }
        
        // Fallback to Git provider if not in localStorage
        const fileData = await unifiedGitDataService.getFileContent(projectId, repositoryUrl, item.path, ref);

        onFileSelect({
          path: item.path,
          name: item.name,
          content: fileData.decodedContent || '',
          size: fileData.size,
          encoding: fileData.encoding,
          lineNumber
        });
      } catch (err) {
        // eslint-disable-next-line no-console
        console.error('Error loading file:', err);
        setError(`Failed to load file: ${err.message}`);
      } finally {
        setLoading(false);
      }
    }
  };

  const handleFileDoubleClick = async (item) => {
    if (item.type === 'tree') {
      return; // Don't open folders in new tab
    }
    
    try {
      setLoading(true);
      const fileData = await unifiedGitDataService.getFileContent(projectId, repositoryUrl, item.path, ref);

      // Open file in new browser tab/window
      const fileContent = fileData.decodedContent || '';
      const blob = new Blob([fileContent], {type: 'text/plain'});
      const url = URL.createObjectURL(blob);
      const newWindow = window.open(url, '_blank');
      
      // Set title of the new window
      if (newWindow) {
        newWindow.document.title = item.name;
        // Clean up the object URL after a delay
        setTimeout(() => URL.revokeObjectURL(url), 100);
      }
    } catch (err) {
      // eslint-disable-next-line no-console
      console.error('Error loading file:', err);
      setError(`Failed to load file: ${err.message}`);
    } finally {
      setLoading(false);
    }
  };

  const handleRefresh = () => {
    // Reset expanded folders and reload the tree
    setExpandedFolders(new Set());
    loadTree('', 0);
  };

  const handleContextMenu = (event, item) => {
    event.preventDefault();
    setContextMenu({
      x: event.clientX,
      y: event.clientY,
      item
    });
    setSelectedItem(item);
  };

  const handleCreateFile = () => {
    setCreateType('file');
    setShowCreateModal(true);
    setContextMenu(null);
  };

  const handleCreateFolder = () => {
    setCreateType('folder');
    setShowCreateModal(true);
    setContextMenu(null);
  };

  const handleCreateItem = async () => {
    if (!newItemName || !miaProjectId || !serviceName) return;

    const basePath = selectedItem?.type === 'tree' ? selectedItem.path : '';
    const fullPath = basePath ? `${basePath}/${newItemName}` : newItemName;

    if (createType === 'file') {
      // Create a new file with empty content
      const newFile = {
        path: fullPath,
        name: newItemName,
        content: '',
        type: 'blob'
      };

      // Save to IndexedDB as a new file (single source of truth)
      await indexedDbFileStorage.saveFile(miaProjectId, serviceName, fullPath, '', {
        originalContent: '',
        fileName: newItemName,
        isNew: true
      });

      // Expand parent folder(s) if needed
      if (basePath) {
        setExpandedFolders(prev => {
          const next = new Set(prev);
          const parts = fullPath.split('/');
          let currentPath = '';
          
          // Loop through all folders in the path (excluding the file itself)
          for (let i = 0; i < parts.length - 1; i++) {
            currentPath = currentPath ? `${currentPath}/${parts[i]}` : parts[i];
            next.add(currentPath);
          }
          return next;
        });
      }

      // Reload tree from IndexedDB (single source of truth)
      await loadTree('', 0);

      // Open the file in editor
      onFileSelect(newFile);
      updateUrlWithFile(fullPath); // Update URL

      // eslint-disable-next-line no-console
      console.log('📄 Created new file:', fullPath);
    } else {
      // Create a new folder
      // Note: Folders don't need to be stored in IndexedDB, they're derived from file paths
      
      // Expand parent folder if needed
      if (basePath) {
        setExpandedFolders(prev => new Set([...prev, basePath]));
      }

      // eslint-disable-next-line no-console
      console.log('📁 Created new folder:', fullPath, '(folders are derived from file paths)');
    }

    setShowCreateModal(false);
    setNewItemName('');
    setSelectedItem(null);
  };

  // Drag and drop file upload handlers
  const handleDragEnter = (e) => {
    e.preventDefault();
    e.stopPropagation();
    dragCounterRef.current++;
    
    if (e.dataTransfer.types.includes('Files')) {
      setIsDraggingFile(true);
    }
  };

  const handleDragLeave = (e) => {
    e.preventDefault();
    e.stopPropagation();
    dragCounterRef.current--;
    
    if (dragCounterRef.current === 0) {
      setIsDraggingFile(false);
      setDragOverFolder(null);
    }
  };

  const handleDragOver = (e, item) => {
    e.preventDefault();
    e.stopPropagation();
    
    if (e.dataTransfer.types.includes('Files')) {
      e.dataTransfer.dropEffect = 'copy';
      
      // Highlight folder being dragged over
      if (item && item.type === 'tree') {
        setDragOverFolder(item.path);
      }
    }
  };

  const handleDrop = async (e, targetItem) => {
    e.preventDefault();
    e.stopPropagation();
    
    dragCounterRef.current = 0;
    setIsDraggingFile(false);
    setDragOverFolder(null);
    
    if (!miaProjectId || !serviceName) {
      console.error('Cannot upload: missing projectId or serviceName'); /* eslint-disable-line no-console */
      return;
    }
    
    const files = Array.from(e.dataTransfer.files);
    if (files.length === 0) return;
    
    // Determine target folder
    const targetFolder = targetItem?.type === 'tree' ? targetItem.path : '';
    
    // eslint-disable-next-line no-console
    console.log(`📤 Uploading ${files.length} file(s) to folder: ${targetFolder || 'root'}`);
    
    for (const file of files) {
      try {
        // Generate file path
        const filePath = targetFolder ? `${targetFolder}/${file.name}` : file.name;
        
        // Read file content
        const content = await readFileContent(file);
        
        // Determine if it's a binary file
        const isBinary = isBinaryFile(file.name, file.type);
        
        // Save to IndexedDB
        await indexedDbFileStorage.saveFile(miaProjectId, serviceName, filePath, content, {
          originalContent: content,
          fileName: file.name,
          isBinary,
          mimeType: file.type,
          size: file.size,
          isNew: true
        });
        
        // eslint-disable-next-line no-console
        console.log(`✅ Uploaded: ${filePath} (${formatFileSize(file.size)}, ${isBinary ? 'binary' : 'text'})`);
      } catch (error) {
        console.error(`Failed to upload ${file.name}:`, error); /* eslint-disable-line no-console */
      }
    }
    
    // Expand target folder if needed
    if (targetFolder) {
      setExpandedFolders(prev => {
        const next = new Set(prev);
        const parts = targetFolder.split('/');
        let currentPath = '';
        
        for (let i = 0; i < parts.length; i++) {
          currentPath = currentPath ? `${currentPath}/${parts[i]}` : parts[i];
          next.add(currentPath);
        }
        return next;
      });
    }
    
    // Reload tree to show new files
    await loadTree('', 0);
    
    // eslint-disable-next-line no-console
    console.log('✅ All files uploaded successfully');
  };

  // Helper function to read file content (supports both text and binary files)
  const readFileContent = (file) => {
    return new Promise((resolve, reject) => {
      const reader = new FileReader();
      
      reader.onload = (event) => {
        const isBinary = isBinaryFile(file.name, file.type);
        
        if (isBinary) {
          // For binary files, convert ArrayBuffer to base64
          const arrayBuffer = event.target.result;
          const base64 = arrayBufferToBase64(arrayBuffer);
          resolve(base64);
        } else {
          // For text files, return as string
          resolve(event.target.result);
        }
      };
      
      reader.onerror = () => reject(reader.error);
      
      // Read as appropriate type
      const isBinary = isBinaryFile(file.name, file.type);
      if (isBinary) {
        reader.readAsArrayBuffer(file);
      } else {
        reader.readAsText(file);
      }
    });
  };

  // Helper to determine if a file is binary based on extension or MIME type
  const isBinaryFile = (fileName, mimeType) => {
    // Image files
    const imageExtensions = ['.png', '.jpg', '.jpeg', '.gif', '.bmp', '.ico', '.svg', '.webp', '.tiff', '.tif'];
    // Document files
    const docExtensions = ['.pdf', '.doc', '.docx', '.xls', '.xlsx', '.ppt', '.pptx'];
    // Archive files
    const archiveExtensions = ['.zip', '.tar', '.gz', '.rar', '.7z'];
    // Media files
    const mediaExtensions = ['.mp3', '.mp4', '.avi', '.mov', '.wav', '.flac', '.ogg'];
    // Binary executables and libraries
    const binaryExtensions = ['.exe', '.dll', '.so', '.dylib', '.bin', '.wasm'];
    // Font files
    const fontExtensions = ['.ttf', '.otf', '.woff', '.woff2', '.eot'];
    
    const allBinaryExtensions = [
      ...imageExtensions,
      ...docExtensions,
      ...archiveExtensions,
      ...mediaExtensions,
      ...binaryExtensions,
      ...fontExtensions
    ];
    
    // Check by extension
    const lowerFileName = fileName.toLowerCase();
    if (allBinaryExtensions.some(ext => lowerFileName.endsWith(ext))) {
      return true;
    }
    
    // Check by MIME type
    if (mimeType) {
      if (mimeType.startsWith('image/') ||
          mimeType.startsWith('audio/') ||
          mimeType.startsWith('video/') ||
          mimeType === 'application/pdf' ||
          mimeType === 'application/zip' ||
          mimeType === 'application/octet-stream' ||
          mimeType.includes('font')) {
        return true;
      }
    }
    
    return false;
  };

  // Helper to convert ArrayBuffer to base64
  const arrayBufferToBase64 = (buffer) => {
    let binary = '';
    const bytes = new Uint8Array(buffer);
    const len = bytes.byteLength;
    for (let i = 0; i < len; i++) {
      binary += String.fromCharCode(bytes[i]);
    }
    return btoa(binary);
  };

  // Helper to format file size
  const formatFileSize = (bytes) => {
    if (bytes === 0) return '0 Bytes';
    const k = 1024;
    const sizes = ['Bytes', 'KB', 'MB', 'GB'];
    const i = Math.floor(Math.log(bytes) / Math.log(k));
    return Math.round(bytes / Math.pow(k, i) * 100) / 100 + ' ' + sizes[i];
  };

  const handleDeleteItem = async () => {
    if (!contextMenu || !miaProjectId || !serviceName) return;

    const item = contextMenu.item;
    
    if (item.type === 'tree') {
      // Delete folder - mark all files in folder for deletion
      const itemsToDelete = tree.filter(treeItem => 
        treeItem.path.startsWith(item.path + '/') && treeItem.type === 'blob'
      );
      
      if (itemsToDelete.length === 0) {
        // Empty folder - just remove from view (folders are derived from files)
        // eslint-disable-next-line no-console
        console.log('📁 Empty folder - no action needed:', item.path);
        setContextMenu(null);
        return;
      }

      // Confirm deletion
      if (!window.confirm(`Delete folder "${item.name}" and all ${itemsToDelete.length} file(s) inside?\n\nThis will mark all files for deletion. They will be removed from Git when you push your changes.`)) {
        setContextMenu(null);
        return;
      }

      // Mark all files for deletion in IndexedDB (single source of truth)
      for (const fileItem of itemsToDelete) {
        await indexedDbFileStorage.markAsDeleted(miaProjectId, serviceName, fileItem.path);
      }

      // Reload tree from IndexedDB to reflect deletions
      await loadTree('', 0);

      // eslint-disable-next-line no-console
      console.log(`🗑️ Marked folder and ${itemsToDelete.length} file(s) for deletion:`, item.path);
    } else {
      // Delete single file
      if (!window.confirm(`Are you sure you want to delete "${item.name}"?\n\nThis will mark the file for deletion. The file will be removed from Git when you push your changes.`)) {
        setContextMenu(null);
        return;
      }

      // Mark file as deleted in IndexedDB (single source of truth)
      await indexedDbFileStorage.markAsDeleted(miaProjectId, serviceName, item.path);
      
      // Reload tree from IndexedDB to reflect deletion
      await loadTree('', 0);
      
      // eslint-disable-next-line no-console
      console.log('🗑️ Marked file for deletion:', item.path);
    }
    
    setContextMenu(null);
  };

  // Check if file needs to be pushed to GitLab
  const needsGitPush = (filePath) => {
    const gitState = fileStates[filePath];
    return gitState === GIT_STATE.MODIFIED || gitState === GIT_STATE.NEW || gitState === GIT_STATE.DELETED;
  };

  // Check if folder contains any modified files (recursively)
  const folderHasModifications = (folderPath) => {
    // Check if any file in fileStates starts with this folder path
    return Object.keys(fileStates).some(filePath => {
      const gitState = fileStates[filePath];
      const isModified = gitState === GIT_STATE.MODIFIED || gitState === GIT_STATE.NEW || gitState === GIT_STATE.DELETED;
      return isModified && filePath.startsWith(folderPath + '/');
    });
  };

  // Check if file has errors
  const hasErrors = (filePath) => {
    return fileErrors && fileErrors[filePath] && fileErrors[filePath].length > 0;
  };

  // Check if folder contains any files with errors (recursively)
  const folderHasErrors = (folderPath) => {
    if (!fileErrors) return false;
    return Object.keys(fileErrors).some(filePath => 
      filePath.startsWith(folderPath + '/') && fileErrors[filePath].length > 0
    );
  };

  // Handle showing problems modal
  const handleShowProblems = () => {
    setShowProblemsModal(true);
  };

  // Get count of files with problems
  const getProblemsCount = () => {
    return Object.keys(fileErrors || {}).length;
  };

  // Get files with errors for display
  const getFilesWithProblems = () => {
    if (!fileErrors) return [];
    
    return Object.entries(fileErrors).map(([filePath, errors]) => ({
      filePath,
      errors,
      errorCount: errors.length
    }));
  };

  // Handle "Ask AI to Fix" button
  const handleAskAIToFix = async (e, item) => {
    e.stopPropagation();
    
    if (!onAskAIToFix || !miaProjectId || !serviceName) return;
    
    try {
      // Get file content from IndexedDB
      const fileData = await indexedDbFileStorage.getFile(miaProjectId, serviceName, item.path);
      
      if (fileData && fileData.content) {
        const errors = fileErrors[item.path] || [];
        const errorDetails = errors.map(err => 
          `Line ${err.startLineNumber}: ${err.message}`
        ).join('\n');
        
        onAskAIToFix(item.path, fileData.content, errorDetails);
      }
    } catch (error) {
      console.error('Error loading file for AI fix:', error);
    }
  };

  const getFileIcon = (item) => {
    if (item.type === 'tree') {
      return expandedFolders.has(item.path) ? 'folder-open' : 'folder';
    }
    
    return getFileIconName(item.name);
  };
  
  const getChevronIcon = (item) => {
    if (item.type !== 'tree') return null;
    return expandedFolders.has(item.path) ? 'chevron-down' : 'chevron-right';
  };
  
  const filterTreeBySearch = (items, query) => {
    if (!query) return items;
    const lowerQuery = query.toLowerCase();
    
    // Find all matching items
    const matchedItems = items.filter(item => {
      const fileName = item.name.toLowerCase();
      const filePath = item.path.toLowerCase();
      return fileName.includes(lowerQuery) || filePath.includes(lowerQuery);
    });
    
    // Collect all parent paths that need to be shown
    const pathsToShow = new Set();
    matchedItems.forEach(item => {
      pathsToShow.add(item.path);
      // Add all parent paths
      let currentPath = item.parent;
      while (currentPath) {
        pathsToShow.add(currentPath);
        const parentItem = items.find(i => i.path === currentPath);
        currentPath = parentItem?.parent;
      }
    });
    
    // Return items that are either matched or are parents of matched items
    return items.filter(item => pathsToShow.has(item.path));
  };
  
  const handleSearchInFiles = () => {
    setActiveTab('search');
  };
  
  const handleShowFiles = () => {
    setActiveTab('files');
    setSearchQuery(''); // Clear search query when switching to files
  };

  if (!repositoryUrl) {
    return (
      <div className="repository-tree-empty">
        <span className="empty-icon">{'📦'}</span>
        <p>{'No GitLab repository linked to this microservice'}</p>
      </div>
    );
  }

  // Show tool-checking spinner while we verify enablement/auth
  if (!gitToolStatus.checked) {
    return (
      <div className="repository-tree-loading">
        <HexagonalLoader
          message="Checking integration status..."
          detailMessage="Verifying your Git provider settings"
        />
      </div>
    );
  }

  // ── BLOCKING: tool is disabled on the server ─────────────────────────────
  if (gitToolStatus.checked && gitToolStatus.toolEnabled === false) {
    const { providerName } = gitToolStatus;
    const providerIconName = providerName === 'GitHub' ? 'github' : providerName === 'Azure DevOps' ? 'azure-devops' : 'gitlab';
    return (
      <div className="git-tool-disabled">
        <div className="git-tool-disabled-icon">
          <Icon name={providerIconName} size={48} glow={true} />
        </div>
        <h3 className="git-tool-disabled-title">{providerName} Integration Disabled</h3>
        <p className="git-tool-disabled-desc">
          The <strong>{providerName}</strong> integration is currently disabled. You need to
          enable it in the <strong>Tools</strong> page before you can browse or download
          this repository.
        </p>
        <div className="git-tool-disabled-steps">
          <div className="git-tool-disabled-step">
            <span className="step-number">1</span>
            <span>Go to <strong>Tools</strong> in your settings</span>
          </div>
          <div className="git-tool-disabled-step">
            <span className="step-number">2</span>
            <span>Enable the <strong>{providerName}</strong> integration</span>
          </div>
          <div className="git-tool-disabled-step">
            <span className="step-number">3</span>
            <span>Come back and reload this page</span>
          </div>
        </div>
        <div className="git-tool-disabled-actions">
          <button
            className="git-tool-disabled-btn primary"
            onClick={() => { window.location.href = '/tools'; }}
            type="button"
          >
            {'🔧 Go to Tools'}
          </button>
          <button
            className="git-tool-disabled-btn secondary"
            onClick={() => {
              setGitToolStatus({ checked: false, toolEnabled: null, authenticated: null, providerName: null });
              checkGitToolStatus(repositoryUrl).then(result => {
                setGitToolStatus({ checked: true, ...result });
              });
            }}
            type="button"
          >
            {'🔄 Recheck Status'}
          </button>
        </div>
      </div>
    );
  }

  // ── BLOCKING: tool enabled but user is not authenticated ─────────────────
  if (gitToolStatus.checked && gitToolStatus.authenticated === false) {
    const { providerName } = gitToolStatus;
    const providerIconName = providerName === 'GitHub' ? 'github' : providerName === 'Azure DevOps' ? 'azure-devops' : 'gitlab';
    return (
      <div className="git-tool-disabled git-tool-not-connected">
        <div className="git-tool-disabled-icon">
          <Icon name={providerIconName} size={48} glow={true} />
        </div>
        <h3 className="git-tool-disabled-title">{providerName} Account Not Connected</h3>
        <p className="git-tool-disabled-desc">
          Your <strong>{providerName}</strong> account is not connected yet. Connect your
          account to browse and download this repository.
        </p>
        <div className="git-tool-disabled-steps">
          <div className="git-tool-disabled-step">
            <span className="step-number">1</span>
            <span>Go to <strong>Tools</strong> in your settings</span>
          </div>
          <div className="git-tool-disabled-step">
            <span className="step-number">2</span>
            <span>Connect your <strong>{providerName}</strong> account</span>
          </div>
          <div className="git-tool-disabled-step">
            <span className="step-number">3</span>
            <span>Come back here — the repository will load automatically</span>
          </div>
        </div>
        <div className="git-tool-disabled-actions">
          <button
            className="git-tool-disabled-btn primary"
            onClick={() => { window.location.href = '/tools'; }}
            type="button"
          >
            {'🔑 Connect Account'}
          </button>
          <button
            className="git-tool-disabled-btn secondary"
            onClick={() => {
              setGitToolStatus({ checked: false, toolEnabled: null, authenticated: null, providerName: null });
              checkGitToolStatus(repositoryUrl).then(result => {
                setGitToolStatus({ checked: true, ...result });
              });
            }}
            type="button"
          >
            {'🔄 Recheck Status'}
          </button>
        </div>
      </div>
    );
  }
  // ─────────────────────────────────────────────────────────────────────────

  if (error) {
    // If pulling from Git, show loader instead of error
    if (isPullingFromGit) {
      return (
        <div className="repository-tree-loading">
          <HexagonalLoader 
            message="Pulling from Git..." 
            progress={gitProgress}
            detailMessage={gitLoadingMessage}
          />
        </div>
      );
    }
    
    // Check if it's an authentication error
    const isAuthError = error.includes('Not authenticated');
    const provider = unifiedGitDataService.detectProvider(repositoryUrl);
    const providerName = provider === 'github' ? 'GitHub' : provider === 'azuredevops' ? 'Azure DevOps' : 'GitLab';
    
    return (
      <div className="repository-tree-error">
        <span className="error-icon">{'⚠️'}</span>
        <p>{error}</p>
        {isAuthError && (
          <p style={{fontSize: '13px', marginTop: '8px', color: '#666'}}>
            Please connect your {providerName} account in the Tools page
          </p>
        )}
        <button
          className="retry-btn"
          onClick={() => {
            if (isAuthError) {
              window.location.href = '/tools';
            } else {
              loadTree('', 0);
            }
          }}
          type="button"
        >
          {isAuthError ? 'Go to Tools' : 'Retry'}
        </button>
      </div>
    );
  }

  return (
    <>
      {/* Create File/Folder Modal */}
      <Modal
        icon={createType === 'file' ? '📄' : '📁'}
        inputLabel={`${createType === 'file' ? 'File' : 'Folder'} name`}
        inputPlaceholder={createType === 'file' ? 'example.js' : 'my-folder'}
        inputValue={newItemName}
        isOpen={showCreateModal}
        message={selectedItem ? `Will be created in: ${selectedItem.path || 'root'}` : 'Will be created in: root'}
        onCancel={() => {
          setShowCreateModal(false);
          setNewItemName('');
          setSelectedItem(null);
        }}
        onClose={() => {
          setShowCreateModal(false);
          setNewItemName('');
          setSelectedItem(null);
        }}
        onConfirm={handleCreateItem}
        onInputChange={setNewItemName}
        title={`Create New ${createType === 'file' ? 'File' : 'Folder'}`}
        type="input"
      />
      
      <div className="repository-tree">
        {/* Git Loading Overlay */}
        {gitLoading && (
          <div className="git-loading-overlay">
            <div className="git-loading-content">
              <div className="git-loading-spinner">
                <Icon name="loader" size={32} />
              </div>
              <div className="git-loading-message">{gitLoadingMessage}</div>
              {gitProgress.total > 0 && (
                <>
                  <div className="git-loading-progress-bar">
                    <div 
                      className="git-loading-progress-fill"
                      style={{ width: `${(gitProgress.current / gitProgress.total) * 100}%` }}
                    />
                  </div>
                  <div className="git-loading-progress-text">
                    {gitProgress.current} / {gitProgress.total} files
                  </div>
                </>
              )}
              {gitPushFiles.length > 0 && (
                <div className="git-push-files-list">
                  <div className="git-push-files-header">Files being pushed:</div>
                  <div className="git-push-files-items">
                    {gitPushFiles.map((file, idx) => (
                      <div key={idx} className={`git-push-file-item ${file.status}`}>
                        <div className="git-push-file-icon">
                          {file.status === 'completed' ? '✓' : '○'}
                        </div>
                        <div className="git-push-file-action">
                          {file.action === 'create' && <span className="action-badge create">NEW</span>}
                          {file.action === 'update' && <span className="action-badge update">MOD</span>}
                          {file.action === 'delete' && <span className="action-badge delete">DEL</span>}
                        </div>
                        <div className="git-push-file-path" title={file.path}>
                          {file.path}
                        </div>
                      </div>
                    ))}
                  </div>
                </div>
              )}
            </div>
          </div>
        )}
        <div className="repository-tree-header">
          <div className="repository-tree-actions">
            <div className="action-tabs">
              <button
                className={`tab-btn search-files ${activeTab === 'files' ? 'active' : ''}`}
                type="button"
                title="Files view"
                onClick={handleShowFiles}
              >
                <Icon name="file" size={14} />
                <span>Files</span>
              </button>
              <button
                className={`tab-btn search-in-files ${activeTab === 'search' ? 'active' : ''}`}
                type="button"
                title="Search in files"
                onClick={handleSearchInFiles}
              >
                <Icon name="search" size={14} />
                <span>Search</span>
              </button>
            </div>
            <div className="header-actions">
              <button
                className="action-icon-btn"
                onClick={handleCreateFile}
                title="Create new file"
                type="button"
              >
                <Icon name="file-plus" size={16} />
              </button>
              <button
                className="action-icon-btn"
                onClick={handleCreateFolder}
                title="Create new folder"
                type="button"
              >
                <Icon name="folder-plus" size={16} />
              </button>
              <button
                className={`action-icon-btn ${getProblemsCount() > 0 ? 'has-problems' : ''}`}
                onClick={handleShowProblems}
                title={`View all files with problems (${getProblemsCount()})`}
                type="button"
                disabled={getProblemsCount() === 0}
              >
                <Icon name="warning" size={16} />
                {getProblemsCount() > 0 && (
                  <span className="problems-badge">{getProblemsCount()}</span>
                )}
              </button>
              <button
                className="action-icon-btn"
                disabled={loading}
                onClick={handleRefresh}
                title="Refresh file tree"
                type="button"
              >
                <Icon name="refresh" size={16} />
              </button>
            </div>
          </div>
        
          {activeTab === 'files' ? (
            <>
              <div className="search-container">
                <Icon name="search" size={14} />
                <input
                  type="text"
                  className="search-input"
                  placeholder="Search files ..."
                  value={searchQuery}
                  onChange={(e) => setSearchQuery(e.target.value)}
                />
                {searchQuery && (
                  <button
                    className="search-clear-btn"
                    onClick={() => setSearchQuery('')}
                    title="Clear search"
                    type="button"
                  >
                    <Icon name="x" size={14} />
                  </button>
                )}
              </div>
        
              <div 
            className={`repository-tree-items ${isDraggingFile ? 'drag-over' : ''}`}
            onDragEnter={handleDragEnter}
            onDragLeave={handleDragLeave}
            onDragOver={(e) => handleDragOver(e, null)}
            onDrop={(e) => handleDrop(e, null)}
          >
            {isDraggingFile && (
              <div className="drag-overlay">
                <div className="drag-overlay-content">
                  <Icon name="upload" size={48} />
                  <p>Drop files here to upload</p>
                  <p className="drag-hint">Files will be added to {dragOverFolder || 'root folder'}</p>
                </div>
              </div>
            )}
            {filterTreeBySearch(tree, searchQuery)
              .filter(item => {
                // If searching, show all filtered items
                if (searchQuery) return true;
                
                // Otherwise, show root level items (level 0)
                if (item.level === 0) return true;
                
                // For nested items, check if ALL parent folders in the hierarchy are expanded
                if (item.parent) {
                  // Build list of all parent paths
                  const pathParts = item.path.split('/');
                  const parentPaths = [];
                  
                  // Create all parent folder paths
                  for (let i = 1; i < pathParts.length; i++) {
                    const parentPath = pathParts.slice(0, i).join('/');
                    parentPaths.push(parentPath);
                  }
                  
                  // Check if ALL parents are expanded
                  return parentPaths.every(parentPath => expandedFolders.has(parentPath));
                }
                
                return false;
              })
              .map((item, idx) => {
                const itemHasModifications = item.type === 'blob' ? needsGitPush(item.path) : folderHasModifications(item.path);
                const itemHasErrors = item.type === 'blob' ? hasErrors(item.path) : folderHasErrors(item.path);
                
                return (
            <button
              className={`tree-item ${selectedFilePath === item.path ? 'selected' : ''} ${item.deleted ? 'deleted' : ''} ${item.isNew ? 'new' : ''} ${itemHasModifications ? 'modified-unpushed' : ''} ${itemHasErrors ? 'has-errors' : ''} ${dragOverFolder === item.path ? 'drag-over-folder' : ''}`}
              key={`${item.path}-${idx}`}
              onClick={() => handleFileClick(item)}
              onContextMenu={(e) => handleContextMenu(e, item)}
              onDoubleClick={() => handleFileDoubleClick(item)}
              onDragOver={(e) => handleDragOver(e, item)}
              onDrop={(e) => handleDrop(e, item)}
              style={{paddingLeft: `${8 + item.level * 16}px`}}
              title={item.deleted ? 'Marked for deletion' : item.isNew ? 'New (not yet committed)' : itemHasErrors && item.type === 'blob' ? 'Has errors - click Fix with AI' : itemHasErrors && item.type === 'tree' ? 'Contains files with errors' : itemHasModifications && item.type === 'blob' ? 'Modified (not pushed to GitLab)' : itemHasModifications && item.type === 'tree' ? 'Contains modified files' : (item.type === 'blob' ? 'Right-click for options | Double-click to open in new tab' : 'Drag files here to upload to this folder')}
              type="button"
            >
            {getChevronIcon(item) && (
              <span className="tree-chevron">
                <Icon name={getChevronIcon(item)} size={12} />
              </span>
            )}
            <span className="tree-icon">
              <Icon name={getFileIcon(item)} size={16} />
            </span>
            <span className="tree-name">{item.name}</span>
            {itemHasErrors && item.type === 'blob' && (
              <span
                className="fix-ai-btn"
                onClick={(e) => handleAskAIToFix(e, item)}
                title="Ask AI to fix errors"
                role="button"
                tabIndex={0}
                onKeyDown={(e) => {
                  if (e.key === 'Enter' || e.key === ' ') {
                    e.preventDefault();
                    handleAskAIToFix(e, item);
                  }
                }}
              >
                <Icon name="sparkles" size={14} />
              </span>
            )}
            {itemHasErrors && (
              <span className="error-indicator" title={item.type === 'blob' ? 'Has errors' : 'Contains files with errors'}>
                <Icon name="warning" size={12} />
              </span>
            )}
            {itemHasModifications && (
              <span className="modified-indicator" title={item.type === 'blob' ? 'Modified (not pushed to GitLab)' : 'Contains modified files'}>
                <Icon name="circle" size={8} />
              </span>
            )}
            {item.deleted && (
              <span className="deleted-badge">
                <Icon name="trash" size={12} />
              </span>
            )}
            {item.isNew && (
              <span className="new-badge">
                <Icon name="plus-circle" size={12} />
              </span>
            )}
          </button>
        );
              })}
              </div>
            </>
          ) : (
            <div className="search-in-files-container">
              <MonacoSearchPanel
                projectId={miaProjectId}
                serviceName={serviceName}
                onFileSelect={(filePath, lineNumber) => {
                  // MonacoSearchPanel passes (filePath, lineNumber) as two params
                  // Convert to file object format that parent expects
                  onFileSelect({
                    path: filePath,
                    name: filePath.split('/').pop(),
                    lineNumber
                  });
                }}
                onClose={() => setActiveTab('files')}
              />
            </div>
          )}
        </div>
      
      {/* Context Menu */}
      {contextMenu && (
        <div
          className="context-menu"
          ref={contextMenuRef}
          style={{
            position: 'fixed',
            top: `${contextMenu.y}px`,
            left: `${contextMenu.x}px`,
            zIndex: 9999
          }}
        >
          {contextMenu.item.type === 'tree' && (
            <>
              <button
                className="context-menu-item"
                onClick={handleCreateFile}
                type="button"
              >
                <Icon name="file-plus" size={14} />
                <span>New File</span>
              </button>
              <button
                className="context-menu-item"
                onClick={handleCreateFolder}
                type="button"
              >
                <Icon name="folder-plus" size={14} />
                <span>New Folder</span>
              </button>
              <div className="context-menu-separator" />
            </>
          )}
          <button
            className="context-menu-item delete"
            onClick={handleDeleteItem}
            type="button"
          >
            <Icon name="trash" size={14} />
            <span>{contextMenu.item.type === 'tree' ? 'Delete Folder' : 'Delete File'}</span>
          </button>
        </div>
      )}
      
      {/* Problems Modal */}
      <Modal
        isOpen={showProblemsModal}
        onClose={() => setShowProblemsModal(false)}
        title="Files with Problems"
        type="custom"
        className="problems-modal"
      >
        <div className="problems-modal-content">
          {getFilesWithProblems().length === 0 ? (
            <div className="no-problems">
              <Icon name="check-circle" size={32} />
              <p>No problems detected in any files</p>
            </div>
          ) : (
            <div className="problems-list">
              <div className="problems-summary">
                <Icon name="warning" size={20} />
                <span>{getFilesWithProblems().length} file(s) with problems</span>
              </div>
              {getFilesWithProblems().map(({filePath, errors, errorCount}) => (
                <div key={filePath} className="problem-file">
                  <div className="problem-file-header" onClick={() => onFileSelect(filePath)}>
                    <Icon name={getFileIconName(filePath)} size={16} />
                    <span className="problem-file-path">{filePath}</span>
                    <span className="problem-count">{errorCount} problem(s)</span>
                  </div>
                  <div className="problem-errors">
                    {errors.map((error, idx) => (
                      <div key={idx} className="problem-error">
                        <span className="problem-line">Line {error.startLineNumber}</span>
                        <span className="problem-message">{error.message}</span>
                      </div>
                    ))}
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>
      </Modal>
    </div>
    </>
  );
};

RepositoryTree.propTypes = {
  fileErrors: PropTypes.object,
  onAskAIToFix: PropTypes.func,
  onFileSelect: PropTypes.func,
  projectId: PropTypes.string,
  repositoryUrl: PropTypes.string,
  selectedFilePath: PropTypes.string,
  serviceName: PropTypes.string,
  isPullingFromGit: PropTypes.bool,
  tenantId: PropTypes.string,
  branchName: PropTypes.string
};

RepositoryTree.defaultProps = {
  fileErrors: {},
  onAskAIToFix: null,
  onFileSelect: () => {},
  projectId: null,
  repositoryUrl: null,
  selectedFilePath: null,
  serviceName: null,
  isPullingFromGit: false,
  tenantId: null,
  branchName: null,
  urlFilePath: null
};

export default RepositoryTree;
